/**
 * Simple confirmation dialog view, with no model. Utilized by the {@link module:app#app:confirm|app:confirm} event
 * @class
 */
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import Base from './alertView';
import Template from '../templates/confirmView.html';

class ConfirmView extends Base {
	/** @override */
	constructor(opts = {}) {
		opts.className = `app-confirm ${opts.className || ''}`;
		opts.events = {
			'click [data-nx-control="yes"]'() {
				this.finish(true);
			},
			'click [data-nx-control="no"]': 'cancel',
			...opts.events,
		};
		opts.headerTitle = opts.headerTitle || i18n.t('i18n.confirm');
		opts.id = opts.id || 'app-confirm';
		opts.modalOptions = {
			blocking: true,
			size: 'md',
			...opts.modalOptions,
		};
		opts.template = opts.template || Template;
		super(opts);

		/**
		 * @type {array}
		 * @default
		 */
		this.args = opts.args || [];

		/**
		 * @type {string}
		 * @default
		 */
		this.classes = opts.classes || '';

		/**
		 * if this.yesEvent or this.noEvent are undefined, this view will build the appropriate
		 * events by appending ':confirmed' or ':cancelled' to this.eventStr .
		 * @type {string}
		 * @default
		 */
		this.eventStr = opts.eventStr;

		/**
		 * Any object that a custom template to confirmView might want to play with:
		 * @type object
		 * @default
		 */
		this.goo = opts.goo;

		/**
		 * @type {string}
		 * @default
		 * @note This is being used as the argument to message.format() -- please document
		 */

		this.itemLabel = opts.itemLabel || '';

		/**
		 * The confirmation message.
		 * @type {string}
		 * @default
		 */
		this.message = opts.message || i18n.t('i18n.areYouSure');

		/**
		 * Event triggered when the user clicks 'no'
		 * @type {string}
		 * @default
		 */
		this.noEvent = opts.noEvent;

		/**
		 * @type {string}
		 * @default
		 */
		this.noLabel = opts.noLabel != null ? opts.noLabel : i18n.t('i18n.no');

		/**
		 * Event triggered when the user clicks 'yes'
		 * @type {string}
		 * @default
		 */
		this.yesEvent = opts.yesEvent;

		/**
		 * @type {string}
		 * @default
		 */
		this.yesLabel = opts.yesLabel != null ? opts.yesLabel : i18n.t('i18n.yes');

		/**
		 * Pass a function that will be fired when the user cancels
		 * @memberOf Netx.Views.ConfirmView
		 * @alias onCancelled
		 * @type {function}
		 */
		this.onCancelled = opts.onCancelled;

		/**
		 * Pass a function that will be fired when the user confirms
		 * @memberOf Netx.Views.ConfirmView
		 * @alias onConfirmed
		 * @type {function}
		 */
		this.onConfirmed = opts.onConfirmed;

		this.title = opts.title;

		// Set on modal options
		this.modalOptions.classNames = this.classes;
	}

	/** @override */
	preRender() {
		super.preRender();

		// Prevent bad goo data from ever causing template errors, as with DAM-6941:
		this.goo = this.goo || {};

		// Additonal template vars
		// Just in case this view ever gets a model - it will lose the benefits it currently has
		Object.assign(this.templateVars, {
			classes: this.classes,
			goo: this.goo,
			headerTitle: this.headerTitle,
			message: this.message,
			itemLabel: this.itemLabel,
			noLabel: this.noLabel,
			yesLabel: this.yesLabel,
		});
	}

	/** @method */
	escape() {
		this.cancel();
	}

	/** @override */
	cancel(e) {
		if (e && e.preventDefault) {
			e.preventDefault();
			e.stopPropagation();
		}

		this.trigger('cancelled', this);
		this.hide();

		const event = this.noEvent || `${this.eventStr}:cancelled`;
		dispatcher.trigger.apply(dispatcher, [event].concat(this.args));

		// If any callbacks were provided, execute them.
		typeof this.onCancelled === 'function' &&
			this.onCancelled.apply(this, this.args);
	}

	/** @method */
	confirm() {
		this.hide();

		const event = this.yesEvent || `${this.eventStr}:confirmed`;
		// return an argument list with the event
		dispatcher.trigger.apply(dispatcher, [event].concat(this.args));

		// If any callbacks were provided, execute them.
		typeof this.onConfirmed === 'function' &&
			this.onConfirmed.apply(this, this.args);
	}

	/**
	 * Handles acceptance or rejection of confirm dialog
	 * @method
	 * @param {boolean} bool Was the confirm accepted or rejected?
	 */
	finish(bool) {
		bool ? this.confirm() : this.cancel();
	}

	/** @type {string} */
	static get __name__() {
		return 'ConfirmView';
	}
}

export default ConfirmView;
